// parent-letter.model.ts
//
// Vertrag B (docs/plans/2026-09-27-elternschreiben.md): dieses Modell ist
// feldgleich zu `functions/src/_shared/parent-letter.model.ts` zu halten —
// bei jeder Änderung beide Kopien anfassen (siehe Student Model Sync in der
// Projekt-CLAUDE.md, hier ohne Parity-Test, weil reine Datenstruktur ohne
// Verhalten).
//
// Abweichung von der Spec: Der Plan nennt `Timestamp` aus `@angular/fire/firestore`
// für die Frontend-Kopie. Das Paket ist in diesem Repo keine Abhängigkeit
// (weder root- noch employee-ui-`package.json`) und wird nirgends verwendet —
// alle bestehenden Modelle importieren `Timestamp` aus `firebase/firestore`
// (z. B. `legal-document.model.ts`, `schools.model.ts`). Diese Datei folgt
// der bestehenden Konvention.
import { Timestamp } from 'firebase/firestore';

export type ParentLetterStatus = 'draft' | 'sending' | 'completed';

export type ParentLetterStatusGroup =
  | 'contractActive' // Vertrag aktiv / Verlängerung offen
  | 'contractTerminating' // Gekündigt, Frist läuft noch
  | 'contractFinalization' // Vertragsabschluss läuft
  | 'registrationOpen'; // Anmeldung offen / Warteliste

export const PARENT_LETTER_STATUS_GROUP_LABELS: Record<
  ParentLetterStatusGroup,
  string
> = {
  contractActive: 'Vertrag aktiv / Verlängerung offen',
  contractTerminating: 'Gekündigt, Frist läuft noch',
  contractFinalization: 'Vertragsabschluss läuft',
  registrationOpen: 'Anmeldung offen / Warteliste',
};

export interface ParentLetterAttachment {
  storagePath: string;
  /** Bucket des Uploads; fehlt er, gilt der Standard-Bucket des Projekts. */
  bucket?: string;
  filename: string;
  size: number;
}

export interface ParentLetterFilter {
  /** leer = alle Kreise */
  kreise: string[];
  /** leer = alle Schulen (innerhalb der Kreise) */
  schoolIds: string[];
  /** mind. 1 */
  statusGroups: ParentLetterStatusGroup[];
  /**
   * Fertig versendete Schreiben, deren Empfänger herausfallen (#1243) — für
   * den Versand in Paketen. Fehlt in Entwürfen von vor dem Feld.
   */
  excludeLetterIds?: string[];
}

export interface ParentLetter {
  id?: string;
  subject: string;
  bodyHtml: string;
  attachments: ParentLetterAttachment[];
  filter: ParentLetterFilter;
  status: ParentLetterStatus;
  /** Familien mit E-Mail beim Absenden */
  recipientCount?: number;
  noEmailCount?: number;
  childCount?: number;
  /** nur Server */
  lastTestSentAt?: Timestamp;
  /** nur Server */
  lastTestSentTo?: string;
  /** nur Server */
  sendingStartedAt?: Timestamp;
  /** nur Server */
  sentAt?: Timestamp;
  sentBy?: string;
  sentByEmail?: string;
  createdOn: Timestamp;
  createdBy: string;
  createdByEmail: string;
  lastModifiedOn: Timestamp;
  lastModifiedBy: string;
  lastModifiedByEmail: string;
  lastModifiedSource: string;
}

/**
 * Dokument `parentLetters/{id}/recipients/{userId}` = `BroadcastRecipientEntry`
 * der Bibliothek (`@solidgroup-agency/firebase-email-service`) plus diese
 * fachlichen Felder (awo-care-Anteil aus Vertrag B).
 */
export interface ParentLetterRecipientData {
  userId: string;
  studentNames: string[];
  schoolNames: string[];
}

export type ParentLetterRecipientStatus =
  | 'pending'
  | 'queued'
  | 'sent'
  | 'deferred'
  | 'delivered'
  | 'bounced'
  | 'failed'
  | 'skipped';

export interface ParentLetterRecipientAttempt {
  n: number;
  kind: 'initial' | 'retry';
  queueId: string;
  at: Timestamp;
}

export interface ParentLetterRecipientEvent {
  type: string;
  at: Timestamp;
  reason?: string;
}

export interface ParentLetterRecipient extends ParentLetterRecipientData {
  id?: string;
  /** bei 'skipped' die Telefonnummer / der Inhalt des Feldes */
  email: string;
  status: ParentLetterRecipientStatus;
  failureKind?: 'transient' | 'permanent';
  lastReason?: string;
  queuedAt?: Timestamp;
  sentAt?: Timestamp;
  deliveryEventAt?: Timestamp;
  attempts: ParentLetterRecipientAttempt[];
  events: ParentLetterRecipientEvent[];
}

// --- Vertrag C: Callable-Request-/Response-Typen -----------------------

export interface PreviewParentLetterRecipientsRequest {
  filter: ParentLetterFilter;
}

export interface PreviewParentLetterRecipientsResponseNoEmailEntry {
  userId: string;
  contact: string;
  studentNames: string[];
  schoolNames: string[];
}

export interface PreviewParentLetterRecipientsResponse {
  familyCount: number;
  childCount: number;
  /** Familien, die ein ausgeschlossenes Schreiben schon bekommen haben (#1243). */
  excludedCount: number;
  noEmail: PreviewParentLetterRecipientsResponseNoEmailEntry[];
}

export interface SendParentLetterTestRequest {
  letterId: string;
}

export interface SendParentLetterTestResponse {
  sentTo: string;
}

export interface SendParentLetterRequest {
  letterId: string;
  expectedRecipientCount: number;
}

export type SendParentLetterResponse =
  | { result: 'started'; recipientCount: number; noEmailCount: number }
  | { result: 'count-mismatch'; recipientCount: number; noEmailCount: number };

export interface RetryParentLetterFailuresRequest {
  letterId: string;
}

export interface RetryParentLetterFailuresResponse {
  retried: number;
}
